import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface AttachmentMetrics {
  id: string;
  filename: string;
  fileType: string;
  fileSize?: number;
  loadStartTime: number;
  loadEndTime?: number;
  renderStartTime?: number;
  renderEndTime?: number;
  usedFallback: boolean;
  fallbackReason?: string;
  errorMessage?: string;
  pageCount?: number;
  currentPage?: number;
}

interface TelemetryContextValue {
  metrics: AttachmentMetrics[];
  isEnabled: boolean;
  isPanelOpen: boolean;
  addMetric: (metric: AttachmentMetrics) => void;
  updateMetric: (id: string, updates: Partial<AttachmentMetrics>) => void;
  clearMetrics: () => void;
  toggleEnabled: () => void;
  togglePanel: () => void;
}

const TelemetryContext = createContext<TelemetryContextValue | null>(null);

interface ProviderProps {
  children: ReactNode;
}

export function AttachmentTelemetryProvider(props: ProviderProps) {
  const [metrics, setMetrics] = useState<AttachmentMetrics[]>([]);
  const [isEnabled, setIsEnabled] = useState(import.meta.env.DEV);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const addMetric = useCallback((metric: AttachmentMetrics) => {
    setMetrics((prev) => [metric, ...prev].slice(0, 50));
  }, []);

  const updateMetric = useCallback((id: string, updates: Partial<AttachmentMetrics>) => {
    setMetrics((prev) =>
      prev.map((m) => (m.id === id ? { ...m, ...updates } : m))
    );
  }, []);

  const clearMetrics = useCallback(() => setMetrics([]), []);
  const toggleEnabled = useCallback(() => setIsEnabled((v) => !v), []);
  const togglePanel = useCallback(() => setIsPanelOpen((v) => !v), []);

  const value: TelemetryContextValue = {
    metrics,
    isEnabled,
    isPanelOpen,
    addMetric,
    updateMetric,
    clearMetrics,
    toggleEnabled,
    togglePanel,
  };

  return (
    <TelemetryContext.Provider value={value}>
      {props.children}
    </TelemetryContext.Provider>
  );
}

export function useAttachmentTelemetry(): TelemetryContextValue {
  const context = useContext(TelemetryContext);
  if (!context) {
    return {
      metrics: [],
      isEnabled: false,
      isPanelOpen: false,
      addMetric: () => {},
      updateMetric: () => {},
      clearMetrics: () => {},
      toggleEnabled: () => {},
      togglePanel: () => {},
    };
  }
  return context;
}

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return Math.round(ms) + 'ms';
  return (ms / 1000).toFixed(2) + 's';
}

export function generateMetricId(): string {
  return Date.now() + '-' + Math.random().toString(36).substr(2, 9);
}
